/**
 * App Component
 *
 * Account settings portal with profile, security, notifications, and regional settings.
 * Uses unified auth portal for authentication.
 */

import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@bloomcode/error-handling';
import { LoadingSpinner, Toaster } from '@bloomcode/ui';
import { useWorkspaceEmbedded } from '@bloomcode/shared-modules';

// Layouts
import { AccountLayout } from './components/layouts/AccountLayout';

// Pages
import HomePage from './pages/HomePage';
import AppsPage from './pages/AppsPage';
import ProfilePage from './pages/ProfilePage';
import SecurityPage from './pages/SecurityPage';
import NotificationsPage from './pages/NotificationsPage';
import RegionalPage from './pages/RegionalPage';
import AppearancePage from './pages/AppearancePage';
import PasswordPage from './pages/PasswordPage';
import PrivacyPage from './pages/PrivacyPage';
import DangerZonePage from './pages/DangerZonePage';

// 404
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

export default function App() {
  const { isEmbedded } = useWorkspaceEmbedded();

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner className="h-screen" />}>
        <Routes>
          {/* Protected account settings routes */}
          <Route element={<AccountLayout />}>
            {/* Home dashboard page */}
            <Route index element={<HomePage />} />

            {/* Account Settings Pages */}
            <Route path="apps" element={<AppsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="regional" element={<RegionalPage />} />
            <Route path="appearance" element={<AppearancePage />} />
            <Route path="password" element={<PasswordPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="danger-zone" element={<DangerZonePage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {/* Hide Toaster when embedded — workspace host provides its own */}
      {!isEmbedded && <Toaster />}
    </ErrorBoundary>
  );
}
